import * as React from 'react';
import { ComponentType } from 'react';
import './icons.scss';
import { themeColors } from '@styles/theme';
import { useTheme } from '@context/themeContext';

export type IconProps = {
    width?: number | string;
    height?: number | string;
    color?: string;
    backgroundColor?: string;
};

export type InnerProps = {
    width: number | string;
    height: number | string;
} & IconProps;

const defProps: InnerProps = {
    width: 24,
    height: 24,
};

export type IconWrapperProps = {
    icon: ComponentType<IconProps>;
} & IconProps;

export const Icon = (Component: ComponentType<InnerProps>) => {
    return (props: IconProps) => {
        const { theme } = useTheme();
        const bgColor = theme === 'light' ? themeColors.white : themeColors.navyBlue;
        const fontColor = theme === 'light' ? themeColors.navyBlue : themeColors.white;

        const { width = defProps.width, height = defProps.height, color, backgroundColor } = props;
        return (
            <Component
                width={width}
                height={height}
                color={color ?? fontColor}
                backgroundColor={backgroundColor ?? bgColor}
            />
        );
    };
};

//TODO remove duplicate components in future (for example Icon above)
export const IconWrapper = ({
    icon,
    width = 24,
    height = 24,
    color,
    // backgroundColor
}: IconWrapperProps) => {
    const { theme } = useTheme();
    const themeColor = theme === 'light' ? themeColors.navyBlue : themeColors.white;
    // const themeBackgroundColor = theme === 'light' ? themeColors.white : themeColors.navyBlue;

    return (
        <div className="icon-wrapper">
            {React.createElement(icon, {
                width,
                height,
                color: color ?? themeColor,
                // backgroundColor:  themeBackgroundColor
            })}
        </div>
    );
};

const ArrowSplitIcon = (props: InnerProps) => {
    return (
        <svg
            width={props.width}
            height={props.height}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M8 18H11V15H2V13H22V15H13V18H16L12 22L8 18ZM12 2L8 6H11V9H2V11H22V9H13V6H16L12 2Z"
                fill={themeColors.textGrey}
            />
        </svg>
    );
};
export const ArrowSplit = Icon(ArrowSplitIcon);

const WindowCloseIcon = (props: InnerProps) => {
    return (
        <svg
            width={props.width}
            height={props.height}
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M6.62571 6L9 8.37429V9H8.37429L6 6.62571L3.62571 9H3V8.37429L5.37429 6L3 3.62571V3H3.62571L6 5.37429L8.37429 3H9V3.62571L6.62571 6Z"
                fill={themeColors.bluePurple}
            />
        </svg>
    );
};
export const WindowClose = Icon(WindowCloseIcon);

const SettingsIcon = (props: InnerProps) => {
    return (
        <svg
            width={props.width}
            height={props.height}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M13.8194 22H10.1794C9.70955 22 9.30299 21.673 9.20243 21.214L8.79543 19.33C8.25248 19.0921 7.73765 18.7946 7.26043 18.443L5.42343 19.028C4.97543 19.1709 4.48842 18.9823 4.25343 18.575L2.42943 15.424C2.19702 15.0165 2.27709 14.5025 2.62243 14.185L4.04743 12.885C3.98263 12.2961 3.98263 11.7019 4.04743 11.113L2.62243 9.816C2.27658 9.49837 2.19648 8.98372 2.42943 8.576L4.24943 5.423C4.48442 5.0157 4.97143 4.82714 5.41943 4.97L7.25643 5.555C7.50049 5.37416 7.75456 5.20722 8.01743 5.055C8.26977 4.91269 8.52947 4.78385 8.79543 4.669L9.20343 2.787C9.3035 2.32797 9.70962 2.00049 10.1794 2H13.8194C14.2892 2.00049 14.6954 2.32797 14.7954 2.787L15.2074 4.67C15.4882 4.79352 15.7617 4.93308 16.0264 5.088C16.2734 5.23081 16.5121 5.38739 16.7414 5.557L18.5794 4.972C19.0271 4.82967 19.5135 5.01816 19.7484 5.425L21.5684 8.578C21.8008 8.98548 21.7208 9.49951 21.3754 9.817L19.9504 11.117C20.0152 11.7059 20.0152 12.3001 19.9504 12.889L21.3754 14.189C21.7208 14.5065 21.8008 15.0205 21.5684 15.428L19.7484 18.581C19.5135 18.9878 19.0271 19.1763 18.5794 19.034L16.7414 18.449C16.5089 18.6203 16.2672 18.7789 16.0174 18.924C15.7553 19.0759 15.4848 19.2131 15.2074 19.335L14.7954 21.214C14.695 21.6726 14.2889 21.9996 13.8194 22ZM7.61943 16.229L8.43943 16.829C8.62428 16.9652 8.81694 17.0904 9.01643 17.204C9.20413 17.3127 9.3974 17.4115 9.59543 17.5L10.5284 17.909L10.9854 20H13.0154L13.4724 17.908L14.4054 17.499C14.8127 17.3194 15.1993 17.0961 15.5584 16.833L16.3794 16.233L18.4204 16.883L19.4354 15.125L17.8524 13.682L17.9644 12.67C18.0136 12.2274 18.0136 11.7806 17.9644 11.338L17.8524 10.326L19.4364 8.88L18.4204 7.121L16.3794 7.771L15.5584 7.171C15.1992 6.90671 14.8127 6.68175 14.4054 6.5L13.4724 6.091L13.0154 4H10.9854L10.5264 6.092L9.59543 6.5C9.39724 6.58704 9.20395 6.68486 9.01643 6.793C8.81817 6.90633 8.62652 7.03086 8.44243 7.166L7.62143 7.766L5.58143 7.116L4.56443 8.88L6.14743 10.321L6.03543 11.334C5.98623 11.7766 5.98623 12.2234 6.03543 12.666L6.14743 13.678L4.56443 15.121L5.57943 16.879L7.61943 16.229ZM11.9954 16C9.78629 16 7.99543 14.2091 7.99543 12C7.99543 9.79086 9.78629 8 11.9954 8C14.2046 8 15.9954 9.79086 15.9954 12C15.9927 14.208 14.2034 15.9972 11.9954 16ZM11.9954 10C10.9028 10.0011 10.0133 10.8788 9.99766 11.9713C9.98201 13.0638 10.846 13.9667 11.9381 13.9991C13.0302 14.0315 13.9463 13.1815 13.9954 12.09V12.49V12C13.9954 10.8954 13.1 10 11.9954 10Z"
                fill={themeColors.navyBlue}
            />
        </svg>
    );
};
export const Settings = Icon(SettingsIcon);

const SearchIcon = (props: InnerProps) => {
    return (
        <svg
            width={props.width}
            height={props.height}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M9.5 3C11.2239 3 12.8772 3.68482 14.0962 4.90381C15.3152 6.12279 16 7.77609 16 9.5C16 11.11 15.41 12.59 14.44 13.73L14.71 14H15.5L20.5 19L19 20.5L14 15.5V14.71L13.73 14.44C12.59 15.41 11.11 16 9.5 16C7.77609 16 6.12279 15.3152 4.90381 14.0962C3.68482 12.8772 3 11.2239 3 9.5C3 7.77609 3.68482 6.12279 4.90381 4.90381C6.12279 3.68482 7.77609 3 9.5 3ZM9.5 5C7 5 5 7 5 9.5C5 12 7 14 9.5 14C12 14 14 12 14 9.5C14 7 12 5 9.5 5Z"
                fill={themeColors.textGrey}
            />
        </svg>
    );
};
export const Search = Icon(SearchIcon);

const QuestionIcon = (props: InnerProps) => {
    return (
        <svg
            width={props.width}
            height={props.height}
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M10 20C4.47967 19.994 0.00606237 15.5204 0 10V9.80002C0.109931 4.30455 4.63459 -0.0720257 10.1307 0.000898217C15.6268 0.0738221 20.0337 4.5689 19.9978 10.0654C19.9619 15.5618 15.4966 19.9989 10 20ZM9.984 18H10C14.4167 17.9956 17.9942 14.4127 17.992 9.99602C17.9898 5.57931 14.4087 2.00002 9.992 2.00002C5.57528 2.00002 1.99421 5.57931 1.992 9.99602C1.98979 14.4127 5.56729 17.9956 9.984 18ZM11 16H9V14H11V16ZM11 13H9C8.9684 11.6977 9.64614 10.4808 10.77 9.82202C11.43 9.31602 12 8.88002 12 8.00002C12 6.89545 11.1046 6.00002 10 6.00002C8.89543 6.00002 8 6.89545 8 8.00002H6V7.91002C6.01608 6.48095 6.79333 5.16902 8.03897 4.46842C9.28461 3.76781 10.8094 3.78496 12.039 4.51342C13.2685 5.24187 14.0161 6.57095 14 8.00002C13.9284 9.07904 13.3497 10.0603 12.44 10.645C11.6177 11.1612 11.0847 12.0328 11 13Z"
                fill={themeColors.navyBlue}
            />
        </svg>
    );
};
export const Question = Icon(QuestionIcon);

const LogoutIcon = (props: InnerProps) => {
    return (
        <svg
            width={props.width}
            height={props.height}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M19 21H10C8.89543 21 8 20.1046 8 19V15H10V19H19V5H10V9H8V5C8 3.89543 8.89543 3 10 3H19C20.1046 3 21 3.89543 21 5V19C21 20.1046 20.1046 21 19 21ZM12 16V13H3V11H12V8L17 12L12 16Z"
                fill={themeColors.navyBlue}
            />
        </svg>
    );
};
export const Logout = Icon(LogoutIcon);

const MenuUpIcon = (props: InnerProps) => {
    return (
        <svg
            width={props.width}
            height={props.height}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path d="M18 15.75L12 9.75L6 15.75L18 15.75Z" fill={themeColors.navyBlue} />
        </svg>
    );
};
export const MenuUp = Icon(MenuUpIcon);

const MenuDownIcon = (props: InnerProps) => {
    return (
        <svg
            width={props.width}
            height={props.height}
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path d="M4 5.5L8 9.5L12 5.5H4Z" fill={themeColors.navyBlue} />
        </svg>
    );
};
export const MenuDown = Icon(MenuDownIcon);

const ChartBoxIcon = (props: InnerProps) => {
    return (
        <svg
            width={props.width}
            height={props.height}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M9 17H7V10H9V17ZM13 17H11V7H13V17ZM17 17H15V13H17V17ZM19 19H5V5H19V19.1V19ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z"
                fill={themeColors.navyBlue}
            />
        </svg>
    );
};
export const ChartBox = Icon(ChartBoxIcon);

const PointIcon = (props: InnerProps) => {
    return (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M12 16C10.9393 16 9.92204 15.5787 9.17193 14.8288C8.42181 14.0789 8.00027 13.0617 8 12.001C7.99973 10.9403 8.42077 9.92294 9.17051 9.17264C9.92025 8.42233 10.9373 8.00053 11.998 8C13.0587 7.99947 14.0762 8.42025 14.8267 9.1698C15.5771 9.91935 15.9992 10.9363 16 11.997C16.0008 13.0577 15.5803 14.0753 14.8309 14.826C14.0815 15.5766 13.0647 15.9989 12.004 16H12Z"
                fill={themeColors.navyBlue}
            />
        </svg>
    );
};
export const Point = Icon(PointIcon);

const LayersIcon = (props: InnerProps) => {
    return (
        <svg
            width={props.width}
            height={props.height}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
        >
            <g>
                <path fill="none" d="M0 0h24v24H0z" />
                <path
                    fill={themeColors.navyBlue}
                    d="M20.083 10.5l1.202.721a.5.5 0 0 1 0 .858L12 17.65l-9.285-5.571a.5.5 0 0 1 0-.858l1.202-.721L12 15.35l8.083-4.85zm0 4.7l1.202.721a.5.5 0 0 1 0 .858l-8.77 5.262a1 1 0 0 1-1.03 0l-8.77-5.262a.5.5 0 0 1 0-.858l1.202-.721L12 20.05l8.083-4.85zM12.514 1.309l8.771 5.262a.5.5 0 0 1 0 .858L12 13 2.715 7.429a.5.5 0 0 1 0-.858l8.77-5.262a1 1 0 0 1 1.03 0z"
                />
            </g>
        </svg>
    );
};
export const Layers = Icon(LayersIcon);

const RouteIcon = (props: InnerProps) => {
    return (
        <svg width={props.width} height={props.height} viewBox="0 0 32 32">
            <g>
                <path
                    fill={themeColors.navyBlue}
                    d="M23.43,13.51c0.16,0.08,0.34,0.13,0.51,0.13c0.18,0,0.35-0.04,0.51-0.13c0.18-0.1,4.37-2.31,4.37-6.63   c0-2.69-2.19-4.88-4.88-4.88c-2.69,0-4.88,2.19-4.88,4.88C19.06,11.2,23.25,13.41,23.43,13.51z M23.84,4.9h0.13   c1.15,0,2.08,0.93,2.08,2.08c0,1.14-0.93,2.08-2.08,2.08s-2.14-0.94-2.14-2.08C21.83,5.82,22.69,4.9,23.84,4.9z"
                />
                <path
                    fill={themeColors.navyBlue}
                    d="M24.32,18.99H7.68c-1.26,0-2.29-1.03-2.29-2.29v-0.45c0-1.26,1.03-2.29,2.29-2.29h9.67c0.61,0,1.11-0.5,1.11-1.11   s-0.49-1.11-1.11-1.11H7.68c-2.48,0-4.5,2.02-4.5,4.5v0.45c0,2.48,2.02,4.5,4.5,4.5h16.63c1.26,0,2.29,1.03,2.29,2.29   s-1.03,2.29-2.29,2.29H13.74c-0.61,0-1.11,0.5-1.11,1.11c0,0.61,0.49,1.11,1.11,1.11h10.58c2.48,0,4.5-2.02,4.5-4.5   S26.8,18.99,24.32,18.99z"
                />
                <path
                    fill={themeColors.navyBlue}
                    d="M10.64,24.64c-0.43-0.43-1.13-0.43-1.56,0L8.13,25.6l-0.95-0.95c-0.43-0.43-1.13-0.43-1.56,0c-0.43,0.43-0.43,1.13,0,1.56   l0.95,0.95l-0.95,0.95c-0.43,0.43-0.43,1.13,0,1.56C5.83,29.89,6.11,30,6.39,30s0.57-0.11,0.78-0.32l0.95-0.95l0.95,0.95   C9.3,29.89,9.58,30,9.86,30s0.57-0.11,0.78-0.32c0.43-0.43,0.43-1.13,0-1.56l-0.95-0.95l0.95-0.95   C11.08,25.77,11.08,25.08,10.64,24.64z"
                />
            </g>
        </svg>
    );
};
export const Route = Icon(RouteIcon);

const PolygonLayerIcon = (props: InnerProps) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={props.width}
            height={props.height}
            viewBox="0 0 24 24"
        >
            <path
                fill={themeColors.navyBlue}
                d="M19,8 C18.4477153,8 18,7.55228475 18,7 L18,6.92895948 L5.99666253,5.08229218 C5.95481263,5.59608198 5.52457637,6 5,6 L5,18 C5.52457637,18 5.95481263,18.403918 5.99666253,18.9177078 L18,17.0710405 L18,17 C18,16.4477153 18.4477153,16 19,16 L19,8 Z M20,8 L20,16 C20.5522847,16 21,16.4477153 21,17 L21,18 C21,18.5522847 20.5522847,19 20,19 L19,19 C18.4754236,19 18.0451874,18.596082 18.0033375,18.0822922 L6,19.9289595 L6,20 C6,20.5522847 5.55228475,21 5,21 L4,21 C3.44771525,21 3,20.5522847 3,20 L3,19 C3,18.4477153 3.44771525,18 4,18 L4,6 C3.44771525,6 3,5.55228475 3,5 L3,4 C3,3.44771525 3.44771525,3 4,3 L5,3 C5.55228475,3 6,3.44771525 6,4 L6,4.07104052 L18.0033375,5.91770782 C18.0451874,5.40391802 18.4754236,5 19,5 L20,5 C20.5522847,5 21,5.44771525 21,6 L21,7 C21,7.55228475 20.5522847,8 20,8 Z M5,19.4910004 L5,19 L4,19 L4,20 L5,20 L5,19.5077646 C4.99990594,19.5021617 4.99990637,19.4965736 5,19.4910022 L5,19.4910004 Z M19,17.5089996 L19,18 L20,18 L20,17 L19,17 L19,17.4922354 C19.0000941,17.4978383 19.0000936,17.5034264 19,17.5089978 L19,17.5089996 Z M5,4.49223544 L5,4 L4,4 L4,5 L5,5 L5,4.5089996 C4.99990637,4.50342635 4.99990594,4.49783831 5,4.49223545 L5,4.49223544 Z M19,6.50776456 L19,7 L20,7 L20,6 L19,6 L19,6.4910004 C19.0000936,6.49657365 19.0000941,6.50216169 19,6.50776455 L19,6.50776456 Z"
            />
        </svg>
    );
};
export const PolygonLayer = Icon(PolygonLayerIcon);

const PolylineLayerIcon = (props: InnerProps) => {
    return (
        <svg width={props.width} height={props.height} viewBox="0 0 24 24">
            <path
                fill={themeColors.navyBlue}
                d="M2 3V9H4.95L6.95 15H6V21H12V16.41L17.41 11H22V5H16V9.57L10.59 15H9.06L7.06 9H8V3M4 5H6V7H4M18 7H20V9H18M8 17H10V19H8Z"
            />
        </svg>
    );
};
export const PolylineLayer = Icon(PolylineLayerIcon);

const Clone = (props: InnerProps) => {
    return (
        <svg
            width="26"
            height="26"
            viewBox="0 0 26 26"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <g clipPath="url(#clip0_109_3363)">
                <path
                    d="M20 20H9.5V9.5H20V20ZM10.6667 18.8333H18.8333V10.6667H10.6667V18.8333ZM8.33333 16.5H6V14.1667H7.16667V15.3333H8.33333V16.5ZM7.16667 13H6V9.5H7.16667V13ZM16.5 8.33333H15.3333V7.16667H14.1667V6H16.5V8.33333ZM7.16667 8.33333H6V6H8.33333V7.16667H7.16667V8.33333ZM13 7.16667H9.5V6H13V7.16667Z"
                    fill={themeColors.white}
                />
            </g>
            <defs>
                <clipPath id="clip0_109_3363">
                    <rect width="14" height="14" fill="white" transform="translate(6 6)" />
                </clipPath>
            </defs>
        </svg>
    );
};

export const CloneIcon = Icon(Clone);

const LineRoutes = ({ width, height, color }: InnerProps) => {
    return (
        <svg
            width={width}
            height={height}
            viewBox="0 0 16 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M14.5456 7.27272C15.3489 7.27272 16.0002 6.6215 16.0002 5.81818C16.0002 5.01485 15.3489 4.36363 14.5456 4.36363C13.7423 4.36363 13.0911 5.01485 13.0911 5.81818C13.0911 6.6215 13.7423 7.27272 14.5456 7.27272ZM14.5454 6.4C14.8667 6.4 15.1272 6.13951 15.1272 5.81818C15.1272 5.49685 14.8667 5.23636 14.5454 5.23636C14.224 5.23636 13.9635 5.49685 13.9635 5.81818C13.9635 6.13951 14.224 6.4 14.5454 6.4Z"
                fill={color}
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.1816 7.27272C10.9849 7.27272 11.6361 6.6215 11.6361 5.81818C11.6361 5.01485 10.9849 4.36363 10.1816 4.36363C9.37827 4.36363 8.72705 5.01485 8.72705 5.81818C8.72705 6.6215 9.37827 7.27272 10.1816 7.27272ZM10.1816 6.4C10.5029 6.4 10.7634 6.13951 10.7634 5.81818C10.7634 5.49685 10.5029 5.23636 10.1816 5.23636C9.86023 5.23636 9.59974 5.49685 9.59974 5.81818C9.59974 6.13951 9.86023 6.4 10.1816 6.4Z"
                fill={color}
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.1816 2.90909C10.9849 2.90909 11.6361 2.25787 11.6361 1.45455C11.6361 0.651222 10.9849 0 10.1816 0C9.37827 0 8.72705 0.651222 8.72705 1.45455C8.72705 2.25787 9.37827 2.90909 10.1816 2.90909ZM10.1816 2.03636C10.5029 2.03636 10.7634 1.77587 10.7634 1.45454C10.7634 1.13321 10.5029 0.87272 10.1816 0.87272C9.86023 0.87272 9.59974 1.13321 9.59974 1.45454C9.59974 1.77587 9.86023 2.03636 10.1816 2.03636Z"
                fill={color}
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M14.5456 2.90909C15.3489 2.90909 16.0002 2.25787 16.0002 1.45455C16.0002 0.651222 15.3489 0 14.5456 0C13.7423 0 13.0911 0.651222 13.0911 1.45455C13.0911 2.25787 13.7423 2.90909 14.5456 2.90909ZM14.5454 2.03636C14.8667 2.03636 15.1272 1.77587 15.1272 1.45454C15.1272 1.13321 14.8667 0.87272 14.5454 0.87272C14.224 0.87272 13.9635 1.13321 13.9635 1.45454C13.9635 1.77587 14.224 2.03636 14.5454 2.03636Z"
                fill={color}
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.81807 7.27272C6.62139 7.27272 7.27262 6.6215 7.27262 5.81818C7.27262 5.01485 6.62139 4.36363 5.81807 4.36363C5.01475 4.36363 4.36353 5.01485 4.36353 5.81818C4.36353 6.6215 5.01475 7.27272 5.81807 7.27272ZM5.81802 6.4C6.13935 6.4 6.39983 6.13951 6.39983 5.81818C6.39983 5.49685 6.13935 5.23636 5.81802 5.23636C5.49669 5.23636 5.2362 5.49685 5.2362 5.81818C5.2362 6.13951 5.49669 6.4 5.81802 6.4Z"
                fill={color}
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M1.45455 7.27272C2.25787 7.27272 2.90909 6.6215 2.90909 5.81818C2.90909 5.01485 2.25787 4.36363 1.45455 4.36363C0.651222 4.36363 0 5.01485 0 5.81818C0 6.6215 0.651222 7.27272 1.45455 7.27272ZM1.45447 6.4C1.7758 6.4 2.03629 6.13951 2.03629 5.81818C2.03629 5.49685 1.7758 5.23636 1.45447 5.23636C1.13315 5.23636 0.872656 5.49685 0.872656 5.81818C0.872656 6.13951 1.13315 6.4 1.45447 6.4Z"
                fill={color}
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4.92407 5.5H2.10589V6.13636H4.92407V5.5Z"
                fill={color}
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9.2876 5.5H6.46942V6.13636H9.2876V5.5Z"
                fill={color}
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M13.6516 1.13636H10.8334V1.77272H13.6516V1.13636Z"
                fill={color}
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8.80298 1.13636H7.56088L5.63367 4.99078L6.20285 5.27537L7.95417 1.77272H8.80298V1.13636Z"
                fill={color}
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M13.6516 5.5H10.8334V6.13636H13.6516V5.5Z"
                fill={color}
            />
        </svg>
    );
};

export const LineRoutesIcon = Icon(LineRoutes);

const EditLine = ({ width, height, color }: InnerProps) => {
    return (
        <svg
            width={width}
            height={height}
            viewBox="0 0 15 13"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M12.3375 2.24552C12.5542 2.02889 12.5542 1.66782 12.3375 1.4623L11.0377 0.162477C10.8322 -0.0541591 10.4711 -0.0541591 10.2545 0.162477L9.2324 1.179L11.3154 3.26205M2.5 7.91696V10H4.58304L10.7266 3.85085L8.64359 1.76781L2.5 7.91696Z"
                fill={color}
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M15 11.5H13.125V12.5H15V11.5ZM12.375 11.5H9.625V12.5H12.375V11.5ZM8.875 11.5H6.125V12.5H8.875V11.5ZM5.375 11.5H2.625V12.5H5.375V11.5ZM1.875 11.5H0V12.5H1.875V11.5Z"
                fill={color}
            />
        </svg>
    );
};

export const EditLineIcon = Icon(EditLine);

const RouteStops = ({ width, height, color }: InnerProps) => {
    return (
        <svg
            width={width}
            height={height}
            viewBox="0 0 14 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M6.22222 0C2.78444 0 0 0.4 0 3.2V11.2C0.00175127 11.5355 0.0718738 11.8669 0.205842 12.1728C0.33981 12.4787 0.534646 12.7523 0.777778 12.976V14.4C0.777778 14.6122 0.859722 14.8157 1.00558 14.9657C1.15145 15.1157 1.34928 15.2 1.55556 15.2H2.33333C2.53961 15.2 2.73744 15.1157 2.88331 14.9657C3.02917 14.8157 3.11111 14.6122 3.11111 14.4V13.6H7.77778C7.31278 12.7405 7.04663 11.7824 7 10.8C7.00693 10.0916 7.17857 9.39511 7.50061 8.76883C7.82264 8.14255 8.2857 7.60463 8.85111 7.2H1.55556V3.2H10.8889V6.4C11.0184 6.3918 11.1483 6.3918 11.2778 6.4C11.6728 6.4013 12.0657 6.46056 12.4444 6.576V3.2C12.4444 0.4 9.66 0 6.22222 0ZM2.72222 9.6C2.95297 9.6 3.17853 9.67038 3.37039 9.80224C3.56224 9.93409 3.71178 10.1215 3.80008 10.3408C3.88838 10.5601 3.91149 10.8013 3.86647 11.0341C3.82146 11.2669 3.71034 11.4807 3.54718 11.6485C3.38402 11.8164 3.17614 11.9306 2.94983 11.9769C2.72352 12.0232 2.48894 11.9995 2.27576 11.9087C2.06258 11.8178 1.88037 11.664 1.75217 11.4667C1.62398 11.2693 1.55556 11.0373 1.55556 10.8C1.55556 10.4817 1.67847 10.1765 1.89726 9.95147C2.11606 9.72643 2.4128 9.6 2.72222 9.6ZM11.2778 8C10.5583 8.00832 9.87059 8.30598 9.36182 8.8293C8.85304 9.3526 8.56364 10.06 8.55556 10.8C8.55556 12.88 11.2778 16 11.2778 16C11.2778 16 14 12.88 14 10.8C13.9919 10.06 13.7025 9.3526 13.1937 8.8293C12.685 8.30598 11.9972 8.00832 11.2778 8ZM11.2778 11.84C11.0302 11.84 10.7928 11.7389 10.6178 11.5588C10.4428 11.3788 10.3444 11.1346 10.3444 10.88C10.3444 10.6254 10.4428 10.3812 10.6178 10.2012C10.7928 10.0211 11.0302 9.92 11.2778 9.92C11.5199 9.93685 11.7479 10.0434 11.9195 10.2199C12.0911 10.3964 12.1947 10.6309 12.2111 10.88C12.2166 11.0076 12.1963 11.1349 12.1513 11.254C12.1064 11.373 12.0378 11.4811 11.95 11.5714C11.8622 11.6617 11.7571 11.7323 11.6414 11.7785C11.5256 11.8247 11.4018 11.8457 11.2778 11.84Z"
                fill={color}
            />
        </svg>
    );
};

export const RouteStopsIcon = Icon(RouteStops);

const EditRoute = ({ width, height, color }: InnerProps) => {
    return (
        <svg
            width={width}
            height={height}
            viewBox="0 0 17 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9.63634 11.3865C10.9417 11.3865 12 12.4447 12 13.7501C12 15.0555 10.9417 16.1137 9.63634 16.1137C8.33094 16.1137 7.27271 15.0555 7.27271 13.7501C7.27271 12.4447 8.33094 11.3865 9.63634 11.3865ZM9.63631 12.8046C10.1585 12.8046 10.5818 13.2279 10.5818 13.7501C10.5818 14.2723 10.1585 14.6956 9.63631 14.6956C9.11415 14.6956 8.69085 14.2723 8.69085 13.7501C8.69085 13.2279 9.11415 12.8046 9.63631 12.8046Z"
                fill={color}
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2.36364 11.3865C3.66904 11.3865 4.72727 12.4447 4.72727 13.7501C4.72727 15.0555 3.66904 16.1137 2.36364 16.1137C1.05824 16.1137 0 15.0555 0 13.7501C0 12.4447 1.05824 11.3865 2.36364 11.3865ZM2.36358 12.8046C2.88574 12.8046 3.30903 13.2279 3.30903 13.7501C3.30903 14.2723 2.88574 14.6956 2.36358 14.6956C1.84142 14.6956 1.41812 14.2723 1.41812 13.7501C1.41812 13.2279 1.84142 12.8046 2.36358 12.8046Z"
                fill={color}
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2.36364 4.11374C3.66904 4.11374 4.72727 5.17197 4.72727 6.47738C4.72727 7.78278 3.66904 8.84101 2.36364 8.84101C1.05824 8.84101 0 7.78278 0 6.47738C0 5.17197 1.05824 4.11374 2.36364 4.11374ZM2.36358 5.53192C2.88574 5.53192 3.30903 5.95521 3.30903 6.47738C3.30903 6.99954 2.88574 7.42283 2.36358 7.42283C1.84142 7.42283 1.41812 6.99954 1.41812 6.47738C1.41812 5.95521 1.84142 5.53192 2.36358 5.53192Z"
                fill={color}
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M1.66364 7.5501V12.8137H3.06364V7.5501H1.66364ZM8.69998 12.9137H3.29998V14.3137H8.69998V12.9137Z"
                fill={color}
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8.5 5.61373H6.5V6.61373H8.5V5.61373ZM5.5 5.61373H3.5V6.61373H5.5V5.61373Z"
                fill={color}
            />
            <path
                d="M16 1.57187C16.1517 1.42022 16.1517 1.16748 16 1.02361L15.0902 0.113734C14.9463 -0.0379114 14.6936 -0.0379114 14.5419 0.113734L13.8265 0.825302L15.2846 2.28343M9.11377 5.54187V7H10.5719L14.8724 2.6956L13.4143 1.23747L9.11377 5.54187Z"
                fill={color}
            />
        </svg>
    );
};

export const EditRouteIcon = Icon(EditRoute);

const RouteDepartures = ({ width, height, color }: InnerProps) => {
    return (
        <svg
            width={width}
            height={height}
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M4.66667 6.3V7.7H3.11111V6.3H4.66667ZM7.77778 6.3V7.7H6.22222V6.3H7.77778ZM10.8889 6.3V7.7H9.33333V6.3H10.8889ZM12.4444 1.4C12.857 1.4 13.2527 1.5475 13.5444 1.81005C13.8361 2.0726 14 2.4287 14 2.8V12.6C14 12.9713 13.8361 13.3274 13.5444 13.5899C13.2527 13.8525 12.857 14 12.4444 14H1.55556C0.692222 14 0 13.37 0 12.6V2.8C0 2.4287 0.163888 2.0726 0.455612 1.81005C0.747335 1.5475 1.143 1.4 1.55556 1.4H2.33333V0H3.88889V1.4H10.1111V0H11.6667V1.4H12.4444ZM12.4444 12.6V4.9H1.55556V12.6H12.4444ZM4.66667 9.1V10.5H3.11111V9.1H4.66667ZM7.77778 9.1V10.5H6.22222V9.1H7.77778ZM10.8889 9.1V10.5H9.33333V9.1H10.8889Z"
                fill={color}
            />
        </svg>
    );
};

export const RouteDeparturesIcon = Icon(RouteDepartures);

const RouteDepartureEdit = ({ width, height, color }: InnerProps) => {
    return (
        <svg
            width={width}
            height={height}
            viewBox="0 0 17 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M16.5 10.5719C16.6516 10.4202 16.6516 10.1675 16.5 10.0236L15.5901 9.11373C15.4463 8.96209 15.1935 8.96209 15.0419 9.11373L14.3264 9.8253L15.7845 11.2834M9.61373 14.5419V16H11.0719L15.3724 11.6956L13.9142 10.2375L9.61373 14.5419Z"
                fill={color}
            />
            <path
                d="M4.66667 6.3V7.7H3.11111V6.3H4.66667ZM7.77778 6.3V7.7H6.22222V6.3H7.77778ZM10.8889 6.3V7.7H9.33333V6.3H10.8889ZM12.4444 1.4C12.857 1.4 13.2527 1.5475 13.5444 1.81005C13.8361 2.0726 14 2.4287 14 2.8V9C14 9 12.7917 10.2374 12.5 10.5C12.5 10.5 9.83333 13.5 9.33333 14H1.55556C0.692222 14 0 13.37 0 12.6V2.8C0 2.4287 0.163888 2.0726 0.455612 1.81005C0.747335 1.5475 1.143 1.4 1.55556 1.4H2.33333V0H3.88889V1.4H10.1111V0H11.6667V1.4H12.4444ZM12.5 10.5L12.4444 4.9H1.55556V12.6H10.6207L11.5647 11.5484L12.5 10.5ZM4.66667 9.1V10.5H3.11111V9.1H4.66667ZM7.77778 9.1V10.5H6.22222V9.1H7.77778ZM10.8889 9.1V10.5H9.33333V9.1H10.8889Z"
                fill={color}
            />
        </svg>
    );
};

export const RouteDepartureEditIcon = Icon(RouteDepartureEdit);

const Map = ({ width, height, color }: InnerProps) => {
    return (
        <svg
            width={width}
            height={height}
            viewBox="0 0 105.93 122.88"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fillRule="evenodd"
                fill={color}
                d="M56.92,73.14a1.62,1.62,0,0,1-1.86.06A65.25,65.25,0,0,1,38.92,58.8,51.29,51.29,0,0,1,28.06,35.37C26.77,27.38,28,19.7,32,13.45a27,27,0,0,1,6-6.66A29.23,29.23,0,0,1,56.36,0,26,26,0,0,1,73.82,7.12a26,26,0,0,1,4.66,5.68c4.27,7,5.19,16,3.31,25.12A55.29,55.29,0,0,1,56.92,73.14Zm-19,.74V101.7l30.15,13V78.87a65.17,65.17,0,0,0,6.45-5.63v41.18l25-12.59v-56l-9.61,3.7a61.61,61.61,0,0,0,2.38-7.81l9.3-3.59A3.22,3.22,0,0,1,105.7,40a3.18,3.18,0,0,1,.22,1.16v62.7a3.23,3.23,0,0,1-2,3L72.72,122.53a3.23,3.23,0,0,1-2.92,0l-35-15.17L4.68,122.53a3.22,3.22,0,0,1-4.33-1.42A3.28,3.28,0,0,1,0,119.66V53.24a3.23,3.23,0,0,1,2.32-3.1L18.7,43.82a58.63,58.63,0,0,0,2.16,6.07L6.46,55.44v59l25-12.59V67.09a76.28,76.28,0,0,0,6.46,6.79ZM55.15,14.21A13.72,13.72,0,1,1,41.43,27.93,13.72,13.72,0,0,1,55.15,14.21Z"
            />
        </svg>
    );
};

export const MapIcon = Icon(Map);

const RoadsEditor = ({ width, height, color }: InnerProps) => {
    return (
        <svg
            width={width}
            height={height}
            viewBox="0 0 17 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M16.3862 8.07187C16.5379 7.92023 16.5379 7.66748 16.3862 7.52361L15.4764 6.61374C15.3325 6.46209 15.0797 6.46209 14.9281 6.61374L14.2126 7.32531L15.6708 8.78344M9.49997 12.0419V13.5H10.9581L15.2586 9.1956L13.8005 7.73747L9.49997 12.0419Z"
                fill={color}
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.241377 3.37932H9.56234C10.054 3.37932 10.2839 3.54208 10.3975 3.66522C10.5335 3.81253 10.6261 4.04066 10.6205 4.30951C10.6149 4.57555 10.5142 4.81319 10.3502 4.97781C10.2046 5.12397 9.91236 5.31036 9.32538 5.31036H4.58621C3.28333 5.3283 2.14916 5.74391 1.31379 6.50627C0.467481 7.27861 0.0252375 8.32332 0.00104832 9.37496C-0.0230829 10.4241 0.369428 11.4834 1.17485 12.2769C1.99212 13.0821 3.13241 13.5173 4.43768 13.5173H8.82519V10.138H4.43768C3.89532 10.138 3.64802 9.96965 3.54655 9.86968C3.43322 9.75803 3.37584 9.61045 3.37947 9.45267C3.38304 9.29741 3.44621 9.13522 3.59176 9.00239C3.72941 8.87677 4.04108 8.68967 4.67464 8.68967H9.41381C10.7624 8.6698 11.9161 8.19415 12.7443 7.36274C13.5722 6.53166 13.9769 5.44171 13.9991 4.38016C14.0212 3.32142 13.6639 2.22196 12.8811 1.37358C12.076 0.501028 10.9183 0 9.56234 0H0.241377V3.37932ZM0.241455 1.61431H0.707503V1.20685H0.241455V1.61431ZM1.6396 1.61431H2.5717V1.20685H1.6396V1.61431ZM3.50379 1.61431H4.43589V1.20685H3.50379V1.61431ZM5.36798 1.61431H6.30008V1.20685H5.36798V1.61431ZM7.23218 1.61431H8.16427V1.20685H7.23218V1.61431ZM9.09637 1.61431H9.56242C9.70731 1.61431 9.84564 1.62338 9.97744 1.64062L10.0249 1.23625C9.8768 1.21687 9.7226 1.20685 9.56242 1.20685H9.09637V1.61431ZM10.7622 1.86759C11.0205 1.99083 11.2414 2.15391 11.4252 2.34529L11.6965 2.05526C11.4802 1.83014 11.2215 1.6395 10.9213 1.49631L10.7622 1.86759ZM11.8867 3.0381C12.0011 3.29642 12.0733 3.57508 12.1025 3.86018L12.4865 3.81641C12.4529 3.48837 12.3697 3.16605 12.2366 2.86563L11.8867 3.0381ZM12.0612 4.71488C12.0051 4.99281 11.9071 5.2621 11.7672 5.50958L12.0988 5.71834C12.2613 5.43107 12.3744 5.11969 12.4389 4.79971L12.0612 4.71488ZM11.2427 6.15553C11.0445 6.32872 10.8114 6.47555 10.5424 6.58596L10.6824 6.96572C10.9895 6.83971 11.2585 6.67078 11.4891 6.46933L11.2427 6.15553ZM9.74354 6.78636C9.61028 6.80155 9.47094 6.80951 9.32546 6.80951H8.73306V7.21698L9.41389 7.21699C9.54134 7.21437 9.66506 7.20514 9.78504 7.19146L9.74354 6.78636ZM4.58629 6.80951C4.4271 6.8125 4.27378 6.82442 4.12637 6.84304L4.17228 7.24762C4.3309 7.22758 4.49835 7.21699 4.67472 7.21699H5.26712V6.80952L4.58629 6.80951ZM6.36348 6.80951V7.21698L7.6367 7.21699V6.80952L6.36348 6.80951ZM8.6322 11.9029H7.7001V12.3103H8.6322V11.9029ZM6.768 11.9029H5.83591V12.3103H6.768V11.9029ZM4.90381 11.9029H4.43776C4.26208 11.9029 4.09595 11.8908 3.93929 11.8681L3.88675 12.2718C4.06161 12.2971 4.24533 12.3103 4.43776 12.3103H4.90381V11.9029ZM3.00992 11.5614C2.70589 11.3905 2.46438 11.1651 2.28423 10.9079L1.97363 11.1501C2.18827 11.4565 2.47398 11.7218 2.82828 11.921L3.00992 11.5614ZM1.91267 9.98659C1.85894 9.65525 1.87768 9.31191 1.96978 8.98473L1.59956 8.86872C1.49055 9.25593 1.46829 9.66247 1.53199 10.0553L1.91267 9.98659ZM2.43998 8.11035C2.64299 7.87591 2.90329 7.67204 3.22327 7.51843L3.06331 7.14755C2.6973 7.32326 2.39405 7.55929 2.15485 7.83551L2.43998 8.11035Z"
                fill={color}
            />
        </svg>
    );
};

export const RoadsEditorIcon = Icon(RoadsEditor);

const RoadEditor = ({ width, height, color }: InnerProps) => {
    return (
        <svg
            width={width}
            height={height}
            viewBox="0 0 17 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M16.8863 9.57187C17.0379 9.42022 17.0379 9.16748 16.8863 9.02361L15.9764 8.11373C15.8325 7.96209 15.5798 7.96209 15.4281 8.11373L14.7127 8.8253L16.1708 10.2834M10 13.5419V15H11.4581L15.7586 10.6956L14.3005 9.23747L10 13.5419Z"
                fill={color}
            />
            <path
                d="M3.6 6.5V7.5H1.2V6.5M7.2 6.5V7.5H4.8V6.5M10.8 6.5V7.5H8.4V6.5M0 3V11H12V3H0Z"
                fill={color}
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0 1.75V0H1V1.75H0ZM0 8.75V5.25H1V8.75H0ZM0 14V12.25H1V14H0Z"
                fill={color}
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11 1.8125V0H12V1.8125H11ZM11 9.0625V5.4375H12V9.0625H11Z"
                fill={color}
            />
        </svg>
    );
};

export const RoadEditorIcon = Icon(RoadEditor);

const RoadSegmentEditor = ({ width, height, color }: InnerProps) => {
    return (
        <svg
            width={width}
            height={height}
            viewBox="0 0 17 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.241377 3.37932H9.56234C10.054 3.37932 10.2839 3.54208 10.3975 3.66522C10.5335 3.81253 10.6261 4.04066 10.6205 4.30951C10.6149 4.57555 10.5142 4.81319 10.3502 4.97781C10.2046 5.12397 9.91236 5.31036 9.32538 5.31036H4.58621C3.28333 5.3283 2.14916 5.74391 1.31379 6.50627C0.467481 7.27861 0.0252375 8.32332 0.00104832 9.37496C-0.0230829 10.4241 0.369428 11.4834 1.17485 12.2769C1.99212 13.0821 3.13241 13.5173 4.43768 13.5173H8.82519V10.138H4.43768C3.89532 10.138 3.64802 9.96965 3.54655 9.86968C3.43322 9.75803 3.37584 9.61045 3.37947 9.45267C3.38304 9.29741 3.44621 9.13522 3.59176 9.00239C3.72941 8.87677 4.04108 8.68967 4.67464 8.68967H9.41381C10.7624 8.6698 11.9161 8.19415 12.7443 7.36274C13.5722 6.53166 13.9769 5.44171 13.9991 4.38016C14.0212 3.32142 13.6639 2.22196 12.8811 1.37358C12.076 0.501028 10.9183 0 9.56234 0H0.241377V3.37932ZM0.241455 1.61431H0.707503V1.20685H0.241455V1.61431ZM1.6396 1.61431H2.5717V1.20685H1.6396V1.61431ZM3.50379 1.61431H4.43589V1.20685H3.50379V1.61431ZM5.36798 1.61431H6.30008V1.20685H5.36798V1.61431ZM7.23218 1.61431H8.16427V1.20685H7.23218V1.61431ZM9.09637 1.61431H9.56242C9.70731 1.61431 9.84564 1.62338 9.97744 1.64062L10.0249 1.23625C9.8768 1.21687 9.7226 1.20685 9.56242 1.20685H9.09637V1.61431ZM10.7622 1.86759C11.0205 1.99083 11.2414 2.15391 11.4252 2.34529L11.6965 2.05526C11.4802 1.83014 11.2215 1.6395 10.9213 1.49631L10.7622 1.86759ZM11.8867 3.0381C12.0011 3.29642 12.0733 3.57508 12.1025 3.86018L12.4865 3.81641C12.4529 3.48837 12.3697 3.16605 12.2366 2.86563L11.8867 3.0381ZM12.0612 4.71488C12.0051 4.99281 11.9071 5.2621 11.7672 5.50958L12.0988 5.71834C12.2613 5.43107 12.3744 5.11969 12.4389 4.79971L12.0612 4.71488ZM11.2427 6.15553C11.0445 6.32872 10.8114 6.47555 10.5424 6.58596L10.6824 6.96572C10.9895 6.83971 11.2585 6.67078 11.4891 6.46933L11.2427 6.15553ZM9.74354 6.78636C9.61028 6.80155 9.47094 6.80951 9.32546 6.80951H8.73306V7.21698L9.41389 7.21699C9.54134 7.21437 9.66506 7.20514 9.78504 7.19146L9.74354 6.78636ZM4.58629 6.80951C4.4271 6.8125 4.27378 6.82442 4.12637 6.84304L4.17228 7.24762C4.3309 7.22758 4.49835 7.21699 4.67472 7.21699H5.26712V6.80952L4.58629 6.80951ZM6.36348 6.80951V7.21698L7.6367 7.21699V6.80952L6.36348 6.80951ZM8.6322 11.9029H7.7001V12.3103H8.6322V11.9029ZM6.768 11.9029H5.83591V12.3103H6.768V11.9029ZM4.90381 11.9029H4.43776C4.26208 11.9029 4.09595 11.8908 3.93929 11.8681L3.88675 12.2718C4.06161 12.2971 4.24533 12.3103 4.43776 12.3103H4.90381V11.9029ZM3.00992 11.5614C2.70589 11.3905 2.46438 11.1651 2.28423 10.9079L1.97363 11.1501C2.18827 11.4565 2.47398 11.7218 2.82828 11.921L3.00992 11.5614ZM1.91267 9.98659C1.85894 9.65525 1.87768 9.31191 1.96978 8.98473L1.59956 8.86872C1.49055 9.25593 1.46829 9.66247 1.53199 10.0553L1.91267 9.98659ZM2.43998 8.11035C2.64299 7.87591 2.90329 7.67204 3.22327 7.51843L3.06331 7.14755C2.6973 7.32326 2.39405 7.55929 2.15485 7.83551L2.43998 8.11035Z"
                fill={color}
            />
            <path
                d="M12.405 14.5592H14.7383V15.337H12.405V14.5592ZM11.6272 11.4481H10.8494V12.2259H11.6272V11.4481ZM12.405 13.7814H14.7383V13.0036H12.405V13.7814ZM12.405 12.2259H14.7383V11.4481H12.405V12.2259ZM11.6272 13.0036H10.8494V13.7814H11.6272V13.0036ZM16.2939 10.6703V16.1148C16.2939 16.5425 15.9439 16.8925 15.5161 16.8925H10.0716C9.64387 16.8925 9.29387 16.5425 9.29387 16.1148V10.6703C9.29387 10.2425 9.64387 9.89253 10.0716 9.89253H15.5161C15.9439 9.89253 16.2939 10.2425 16.2939 10.6703ZM15.5161 10.6703H10.0716V16.1148H15.5161V10.6703ZM11.6272 14.5592H10.8494V15.337H11.6272V14.5592Z"
                fill={color}
            />
        </svg>
    );
};

export const RoadSegmentEditorIcon = Icon(RoadSegmentEditor);

const Layers1 = ({ width, height, color }: InnerProps) => {
    return (
        <svg
            width={width}
            height={height}
            viewBox="0 0 14 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M7 11.0078L12.7244 6.55111L14 5.56333L7 0.118889L0 5.56333L1.26778 6.55111M7 12.9833L1.26 8.52667L0 9.50667L7 14.9511L14 9.50667L12.7322 8.51889L7 12.9833Z"
                fill={color}
            />
        </svg>
    );
};

export const Layers1Icon = Icon(Layers1);

const GroupSelection = ({ width, height, color }: InnerProps) => {
    return (
        <svg
            width={width}
            height={height}
            viewBox="0 0 15 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2.15849 0.719495H0.719495V2.15849H2.15849V0.719495ZM0 0V2.87798H2.87798V0H0Z"
                fill={color}
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2.15849 12.2314H0.719495V13.6704H2.15849V12.2314ZM0 11.5119V14.3899H2.87798V11.5119H0Z"
                fill={color}
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M13.6702 0.719495H12.2312V2.15849H13.6702V0.719495ZM11.5117 0V2.87798H14.3897V0H11.5117Z"
                fill={color}
            />
            <path
                d="M12.4544 12.1595C12.771 11.663 12.9509 11.0658 12.9509 10.4327C12.9509 8.63394 11.5119 7.19495 9.71317 7.19495C7.91443 7.19495 6.47544 8.63394 6.47544 10.4327C6.47544 12.2314 7.91443 13.6704 9.71317 13.6704C10.3391 13.6704 10.9291 13.4905 11.4256 13.1812L13.6704 15.39L14.6705 14.3899L12.4544 12.1595ZM9.71317 12.2314C8.72027 12.2314 7.91443 11.4256 7.91443 10.4327C7.91443 9.43978 8.72027 8.63394 9.71317 8.63394C10.7061 8.63394 11.5119 9.43978 11.5119 10.4327C11.5119 11.4256 10.7061 12.2314 9.71317 12.2314ZM13.6704 6.47546H12.2314V2.51824H13.6704L13.6704 6.47546ZM12.2314 6.49704V6.47546H13.6704L13.6704 7.91445H13.6488C13.2819 7.34605 12.7998 6.86399 12.2314 6.49704ZM0.719482 2.87798H2.15847V11.8717H0.719482V2.87798ZM2.51822 12.2314H5.3962C5.70354 12.88 5.94734 13.1874 6.47544 13.6704H2.51822V12.2314ZM11.5119 2.15849H2.51822V0.719498H11.5119V2.15849Z"
                fill={color}
            />
            <path
                d="M6.83513 3.74137H7.55462V5.46816H6.83513M4.31689 11.5119H5.03639C4.67664 8.27419 6.47538 5.75596 10.0729 6.1157V2.87798H4.31689V11.5119Z"
                fill={color}
            />
        </svg>
    );
};

export const GroupSelectionIcon = Icon(GroupSelection);
